import { Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { ExtensionPoint, EXTENSION_POINTS } from "src/components/PuckExtensions";
import { FoodsIllustrations, WhiteBox } from "@foodpilot/foods";
import { NoPostSupplier } from "./IncompleteData/NoPostSupplier";
import { SsqShadowedBySupplierReference } from "./IncompleteData/SsqShadowedBySupplierReference";
import { findViolationByCode, INGREDIENT_SUPPLIER_HAS_RESOLVABLE_REFERENCE } from "src/utils/apiViolations";
import { SsqSsqHeading } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { useIngredientContext } from "../useIngredientContext";
import { URLHandler } from "src/services/URLHandler";
import { Fragment } from "react/jsx-runtime";
import { ScoreChart } from "src/components/ScoreChart/ScoreChart";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { Loading } from "src/components/utils/Loading";
import { NoIngredientReference } from "./IncompleteData/NoIngRef";
import { QuestionnairesList } from "src/components/QuestionnairesList/QuestionnairesList";
import { CompanySelection } from "src/components/CompanySelection/CompanySelection";
import { getSameLevelDimensionsChartData } from "src/utils/questionnaire";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";

export const Questionnaire = () => {
    const { selectedCompany } = useFoodpilotContext();
    const { scoresWithVariations, lastSelectedVariation } = useEntityScores();
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isProductUxV2 = useIsProductUxV2();
    const {
        ingredient,
        isReady,
        ssqVersion,
        isSsqVersionReady,
        posts,
        activeCompanyId,
        activePost,
        isActivePostRefetching,
        isActivePostLoading,
        activePostError,
        canModifySSQ,
        supplierCompaniesWithActiveCompanyFirst,
        isIngredientReferenceReady,
    } = useIngredientContext();

    // The backend lazily creates the SsqPost on GET; if the supplier is shadowed
    // by an IngredientReference holding scores for the active campaign, the GET
    // returns 422 with this exact code. We swap the QSS form for a friendly
    // explainer instead of leaking a raw error toast.
    const supplierShadowingViolation = findViolationByCode(
        activePostError,
        INGREDIENT_SUPPLIER_HAS_RESOLVABLE_REFERENCE,
    );

    if (!isReady || !isIngredientReferenceReady || isActivePostLoading || !isSsqVersionReady) {
        return <Loading />;
    }

    if (ingredient === undefined) {
        return;
    }

    if (ingredient.ingredientReferenceId === undefined) {
        return <NoIngredientReference />;
    }

    if (selectedCompany === null) return <Loading />;

    const supplierSelection =
        supplierCompaniesWithActiveCompanyFirst.length > 1 ?
            <CompanySelection
                appearance={isProductUxV2 ? "product-v2" : "legacy"}
                companies={supplierCompaniesWithActiveCompanyFirst}
                selectedCompanyId={activeCompanyId}
                onChange={(company) => {
                    if (!company) return;

                    navigate(URLHandler.ingredient.ssq(selectedCompany.id, ingredient.id, company.id));
                }}
                posts={posts}
            />
        :   null;

    const hasNoActivePost = activePost === null || activePost === undefined;
    const shouldShowNoPostSupplier =
        posts !== undefined && isActivePostLoading === false && hasNoActivePost && canModifySSQ === false;

    if (ssqVersion === undefined) {
        return (
            <Stack gap={theme.spacing(2)}>
                <ExtensionPoint extensionPoint={EXTENSION_POINTS.INGREDIENT_DETAIL_QUESTIONNAIRE_HEADER} />
                <ExtensionPoint extensionPoint={EXTENSION_POINTS.QUESTIONNAIRE_BEFORE} />
                {supplierSelection}
                {shouldShowNoPostSupplier ?
                    <NoPostSupplier />
                :   <WhiteBox>
                        <Stack
                            flexDirection={"column"}
                            padding={theme.spacing(4, 8)}
                            gap={theme.spacing(2)}
                            justifyContent={"center"}
                            alignItems={"center"}
                        >
                            <Fragment>
                                <FoodsIllustrations illustration={"underConstruction"} />
                                <Typography variant="body-medium">
                                    {t(
                                        "ingredient.no_ssq_id_on_ing_ref",
                                        "You don't have any SSQ assigned to chosen selected reference.",
                                    )}
                                </Typography>
                            </Fragment>
                        </Stack>
                    </WhiteBox>
                }
                <ExtensionPoint extensionPoint={EXTENSION_POINTS.QUESTIONNAIRE_AFTER} />
                <ExtensionPoint extensionPoint={EXTENSION_POINTS.INGREDIENT_DETAIL_QUESTIONNAIRE_FOOTER} />
            </Stack>
        );
    }

    const { data, totalScore, maxScore, specificityRate } = getSameLevelDimensionsChartData(
        activePost?.olapScores[lastSelectedVariation.scoreId],
        ["headings", "ssq_versions"],
        { ssqVersion },
    );

    return (
        <Stack gap={theme.spacing(2)}>
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.INGREDIENT_DETAIL_QUESTIONNAIRE_HEADER} />
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.QUESTIONNAIRE_BEFORE} />
            {/* <Typography variant="h2">{questionnaireTitle}</Typography> */}
            {supplierSelection}
            <Stack gap={theme.spacing(3.5)}>
                {supplierShadowingViolation !== null ?
                    <SsqShadowedBySupplierReference hint={supplierShadowingViolation.hint} />
                : shouldShowNoPostSupplier ?
                    <NoPostSupplier />
                :   <QuestionnairesList
                        ssqVersion={ssqVersion}
                        exportSubject={{ type: "ingredient", id: ingredient.id, name: ingredient.name }}
                        post={activePost}
                        isSsqLoading={false}
                        isPostLoading={isActivePostRefetching}
                        completeQuestionnaireUrl={(ssqSsqHeading: SsqSsqHeading) => {
                            const url = URLHandler.ingredient.ssqHeading(
                                selectedCompany.id,
                                ingredient.id,
                                activeCompanyId ?? 0,
                                ssqSsqHeading.id,
                            );
                            return url;
                        }}
                        scoresSection={
                            <ScoreChart
                                appearance={isProductUxV2 ? "product-v2" : "legacy"}
                                scoresWithVariations={scoresWithVariations}
                                selectedVariation={lastSelectedVariation}
                                totalScore={totalScore}
                                data={data || undefined}
                                maxScore={maxScore}
                                specificityRate={specificityRate}
                                isLoading={isActivePostLoading || isActivePostRefetching}
                            />
                        }
                        title={t("questionnaire.ingredient.title", "Questionnaire from supplier")}
                    />
                }
            </Stack>
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.QUESTIONNAIRE_AFTER} />
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.INGREDIENT_DETAIL_QUESTIONNAIRE_FOOTER} />
        </Stack>
    );
};
